/**
 * Workspaces: a user belongs to one or more (ids on the auth record, `users.workspaces`) and
 * works in one at a time. The current one is remembered per user in localStorage; every other
 * data module scopes its queries with currentWorkspaceId(). Membership is changed only through
 * the server routes in server/pb_hooks/workspaces.pb.js.
 */
import type { InvitationsResponse, WorkspacesResponse } from "@/lib/core/pb-types";
import { pb } from "@/lib/core/pocketbase";

export type Member = { id: string; name: string; email: string; owner: boolean };

const storageKey = (userId: string) => `blanqo.workspace.${userId}`;

function myWorkspaceIds(): string[] {
  return (pb.authStore.record?.workspaces as string[] | undefined) ?? [];
}

function readStored(userId: string): string | null {
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

/** The workspace the user works in: the remembered one if still a member, else the first. */
export function currentWorkspaceId(): string {
  const ids = myWorkspaceIds();
  const stored = pb.authStore.record ? readStored(pb.authStore.record.id) : null;
  const id = stored && ids.includes(stored) ? stored : ids[0];
  if (!id) throw new Error("No workspace for the current user");
  return id;
}

export function setCurrentWorkspaceId(id: string): void {
  const userId = pb.authStore.record?.id;
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), id);
  } catch {
    // private mode: the choice lasts until reload
  }
}

/** Reloads the auth record after membership changed on the server. */
async function refreshMembership() {
  await pb.collection("users").authRefresh();
}

export async function listWorkspaces(): Promise<WorkspacesResponse[]> {
  return pb.collection("workspaces").getFullList<WorkspacesResponse>({ sort: "name" });
}

export async function getWorkspace(): Promise<WorkspacesResponse> {
  return pb.collection("workspaces").getOne<WorkspacesResponse>(currentWorkspaceId());
}

export async function renameWorkspace(name: string) {
  return pb.collection("workspaces").update(currentWorkspaceId(), { name: name.trim() });
}

/** Creates a workspace owned by the user and switches to it. */
export async function createWorkspace(name: string): Promise<WorkspacesResponse> {
  const workspace = await pb.send<WorkspacesResponse>("/api/blanqo/workspaces", {
    method: "POST",
    body: { name: name.trim() },
  });
  await refreshMembership();
  setCurrentWorkspaceId(workspace.id);
  return workspace;
}

/**
 * Deletes the current workspace with all its data (owner only). The password confirms it;
 * `retention` confirms the owner keeps their own copies of its finalised invoices (refused
 * without it while there are any).
 */
export async function deleteWorkspace(input: {
  password: string;
  retention: boolean;
}): Promise<void> {
  await pb.send(`/api/blanqo/workspaces/${encodeURIComponent(currentWorkspaceId())}/delete`, {
    method: "POST",
    body: input,
  });
  await refreshMembership();
}

export async function listMembers(): Promise<Member[]> {
  return pb.send<Member[]>(
    `/api/blanqo/workspaces/${encodeURIComponent(currentWorkspaceId())}/members`,
    { method: "GET" },
  );
}

/** Owner removes a member, or a member removes themselves (leave). */
export async function removeMember(userId: string): Promise<void> {
  const workspace = encodeURIComponent(currentWorkspaceId());
  await pb.send(
    `/api/blanqo/workspaces/${workspace}/members/${encodeURIComponent(userId)}/remove`,
    {
      method: "POST",
    },
  );
  if (userId === pb.authStore.record?.id) await refreshMembership();
}

export async function inviteMember(email: string): Promise<InvitationsResponse> {
  return pb.send<InvitationsResponse>(
    `/api/blanqo/workspaces/${encodeURIComponent(currentWorkspaceId())}/invitations`,
    { method: "POST", body: { email: email.trim() } },
  );
}

/** Open invitations of the current workspace (owner only; others get an empty list). */
export async function listSentInvitations(): Promise<InvitationsResponse[]> {
  return pb.collection("invitations").getFullList<InvitationsResponse>({
    filter: pb.filter("workspace = {:workspace}", { workspace: currentWorkspaceId() }),
    sort: "email",
  });
}

/** Invitations addressed to the signed-in user. */
export async function listMyInvitations(): Promise<InvitationsResponse[]> {
  return pb.collection("invitations").getFullList<InvitationsResponse>({
    filter: pb.filter("email = {:email}", {
      email: String(pb.authStore.record?.email ?? "").toLowerCase(),
    }),
    sort: "-created",
  });
}

/** Revoke (owner) or decline (invited person). */
export async function deleteInvitation(id: string): Promise<void> {
  await pb.collection("invitations").delete(id);
}

/** Joins the workspace and switches to it. */
export async function acceptInvitation(id: string): Promise<WorkspacesResponse> {
  const workspace = await pb.send<WorkspacesResponse>(
    `/api/blanqo/invitations/${encodeURIComponent(id)}/accept`,
    { method: "POST" },
  );
  await refreshMembership();
  setCurrentWorkspaceId(workspace.id);
  return workspace;
}
